import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { BRAND, SUPPORT, CTA_LINKS } from "@/data/content";

const Section = ({ title, children }) => (
  <div className="mt-8">
    <h2 className="font-display text-xl font-semibold tracking-tight text-brand-navy md:text-2xl">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-brand-navy/70 md:text-base">{children}</div>
  </div>
);

export default function PrivacyPolicy() {
  useEffect(() => window.scrollTo(0, 0), []);
  const updated = "Junho de 2026"; // EDITÁVEL

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* header */}
      <header className="border-b border-brand-navy/10 bg-brand-navy py-5 text-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 lg:px-8">
          <Link to="/" data-testid="privacy-back" className="flex items-center gap-2.5">
            <img src={BRAND.logo} alt="Transferência Academy" className="h-9 brightness-0 invert" />
            <span className="flex flex-col items-start leading-none">
              <span className="font-display text-sm font-semibold tracking-tight">{BRAND.name}</span>
              <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.3em] text-brand-gold">{BRAND.submark}</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs font-medium transition-colors hover:bg-white/10"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Voltar ao site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-14 lg:px-8 lg:py-20">
        <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-goldLight px-4 py-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-brand-goldDark" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-goldDark">Privacidade</span>
        </div>
        <h1 className="mt-6 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-navy sm:text-4xl lg:text-5xl">
          Política de Privacidade
        </h1>
        <p className="mt-3 text-sm text-brand-navy/50">Última atualização: {updated}</p>

        <div className="mt-6 rounded-2xl border border-brand-navy/10 bg-brand-mist/50 p-5 text-sm leading-relaxed text-brand-navy/70">
          Esta Política de Privacidade descreve como a <strong>Transferência Academy</strong> coleta, utiliza e
          protege as informações dos usuários que acessam esta página e o produto <strong>Intensivo Transferência
          UNESP</strong>, em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD).
        </div>

        <Section title="1. Dados que coletamos">
          <p>Podemos coletar as seguintes informações:</p>
          <ul className="ml-5 list-disc space-y-1.5">
            <li><strong>Dados fornecidos por você:</strong> nome, e-mail e telefone/WhatsApp, quando você entra em contato ou demonstra interesse no Intensivo.</li>
            <li><strong>Dados de navegação:</strong> informações coletadas automaticamente por cookies e tecnologias similares, como páginas visitadas, tempo de permanência, dispositivo e origem do acesso.</li>
          </ul>
        </Section>

        <Section title="2. Cookies e ferramentas de terceiros">
          <p>
            Utilizamos ferramentas de análise e marketing, incluindo o <strong>Meta Pixel (Facebook/Instagram)</strong>,
            para medir o desempenho de campanhas, entender o comportamento de navegação e exibir anúncios mais relevantes.
            Essas tecnologias podem coletar dados por meio de cookies. Você pode gerenciar ou desativar os cookies nas
            configurações do seu navegador.
          </p>
        </Section>

        <Section title="3. Como utilizamos seus dados">
          <ul className="ml-5 list-disc space-y-1.5">
            <li>Responder a solicitações, dúvidas e atendimento via WhatsApp;</li>
            <li>Enviar informações sobre o Intensivo Transferência UNESP e comunicações relacionadas;</li>
            <li>Melhorar a experiência de navegação e o conteúdo oferecido;</li>
            <li>Mensurar e otimizar campanhas de marketing e anúncios.</li>
          </ul>
        </Section>

        <Section title="4. Compartilhamento de dados">
          <p>
            Não vendemos seus dados pessoais. As informações podem ser compartilhadas apenas com provedores de serviço
            que apoiam nossa operação (por exemplo, plataformas de pagamento, hospedagem, e-mail e mídia), sempre
            limitados à finalidade necessária e respeitando a legislação aplicável.
          </p>
        </Section>

        <Section title="5. Seus direitos (LGPD)">
          <p>
            Você pode, a qualquer momento, solicitar acesso, correção, atualização, portabilidade ou exclusão dos seus
            dados, bem como revogar consentimentos. Para exercer esses direitos, entre em contato pelos canais abaixo.
          </p>
        </Section>

        <Section title="6. Segurança">
          <p>
            Adotamos medidas técnicas e organizacionais razoáveis para proteger seus dados contra acesso não autorizado,
            perda ou uso indevido. Nenhum método de transmissão pela internet é 100% seguro, mas nos comprometemos a
            proteger suas informações da melhor forma possível.
          </p>
        </Section>

        <Section title="7. Contato">
          <p>Em caso de dúvidas sobre esta Política de Privacidade ou sobre o tratamento dos seus dados, fale conosco:</p>
          <ul className="ml-5 list-disc space-y-1.5">
            <li>WhatsApp: <strong>{SUPPORT.phoneLabel}</strong> — <a href={CTA_LINKS.whatsapp} target="_blank" rel="noopener noreferrer" className="text-brand-goldDark underline">abrir conversa</a></li>
            <li>Instagram: <a href={CTA_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="text-brand-goldDark underline">@transferencia_academy</a></li>
            <li>E-mail: <strong>contato@transferenciaacademy.com.br</strong> {/* EDITÁVEL */}</li>
          </ul>
        </Section>

        <Section title="8. Alterações desta Política">
          <p>
            Esta Política poderá ser atualizada periodicamente. A versão mais recente estará sempre disponível nesta
            página, com a data da última atualização indicada no topo.
          </p>
        </Section>

        <div className="mt-12 border-t border-brand-navy/10 pt-6 text-xs text-brand-navy/40">
          © {new Date().getFullYear()} Transferência Academy. Todos os direitos reservados.
        </div>
      </main>
    </div>
  );
}
